/**
 * Copyright 2025 Lumavec Group LLC
 *
 * Delete worktree confirmation dialog component
 */

import React from 'react';
import { Trash2, Loader, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';

interface DeleteWorktreeDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  worktreeName: string;
  hasUnmergedChanges: boolean;
  uncommittedFilesCount: number;
  unpushedCommitsCount: number;
  onConfirm: () => void;
  deleting: boolean;
}

export function DeleteWorktreeDialog({
  isOpen,
  onOpenChange,
  worktreeName,
  hasUnmergedChanges,
  uncommittedFilesCount,
  unpushedCommitsCount,
  onConfirm,
  deleting
}: DeleteWorktreeDialogProps) {
  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {hasUnmergedChanges && (
              <AlertTriangle size={20} className="text-amber-500" />
            )}
            Delete Worktree
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                Are you sure you want to delete the worktree <strong className="text-gray-900">{worktreeName}</strong>?
              </p>
              
              {hasUnmergedChanges && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                  <p className="text-amber-800 font-medium mb-2">
                    Warning: This worktree has unmerged changes
                  </p>
                  <ul className="text-amber-700 text-sm space-y-1">
                    {uncommittedFilesCount > 0 && (
                      <li>• {uncommittedFilesCount} uncommitted file{uncommittedFilesCount !== 1 ? 's' : ''}</li>
                    )}
                    {unpushedCommitsCount > 0 && (
                      <li>• {unpushedCommitsCount} unpushed commit{unpushedCommitsCount !== 1 ? 's' : ''}</li>
                    )}
                  </ul>
                  <p className="text-amber-700 text-sm mt-2">
                    These changes will be permanently lost if you proceed.
                  </p>
                </div>
              )}
              
              {!hasUnmergedChanges && (
                <p className="text-gray-600 text-sm">
                  This action cannot be undone.
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700"
          >
            {deleting ? (
              <>
                <Loader size={14} className="animate-spin mr-2" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 size={14} className="mr-2" />
                {hasUnmergedChanges ? 'Delete Anyway' : 'Delete'}
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
